// Shared post-registration payment prompt.
//
// Rendered ONCE per successful registration action (never on plain page
// visits) when ALL of the following are true:
//   • payment is enabled for the event
//   • the viewer is personally registered (named-only partners excluded)
//   • the viewer is not yet paid
//   • an entry fee > 0 is configured
//
// Copy is uniform across Club Events, Tournaments, and Round Robins:
//   "You're registered!
//    This {event type} has an entry fee of {amount}.
//    You can pay now or anytime before the event begins."
//
// Actions:
//   • Pay {amount} Now  → parent closes prompt, smooth-scrolls to the
//                         event payment section and auto-triggers the
//                         existing StripeEmbeddedPay open action (Stripe
//                         path). For Venmo/Zelle events the parent opens
//                         the external link in a new tab.
//   • I'll Pay Later    → closes the prompt. Registration remains intact.
//                         Payment Due state continues to show on-page.
//
// Cash path: the prompt collapses into a static "Payment Due at Event"
// info line and shows a single dismiss button — no fake Pay Now.

import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { CheckCircle, CreditCard, ArrowSquareOut } from '@phosphor-icons/react';

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {(v:boolean)=>void} props.onOpenChange
 * @param {string}   props.eventTypeLabel        "Club Event" | "Tournament" | "Round Robin"
 * @param {string}   props.amountLabel           Formatted amount string, e.g. "$25"
 * @param {'stripe'|'cash'|'venmo'|'zelle'} props.paymentMethod
 * @param {()=>void} props.onPayNow              Parent handles scroll + auto-open Stripe / external link.
 * @param {()=>void} props.onPayLater            Parent closes the prompt.
 */
export default function PostRegistrationPaymentPrompt({
  open,
  onOpenChange,
  eventTypeLabel,
  amountLabel,
  paymentMethod,
  onPayNow,
  onPayLater,
}) {
  const isCash = paymentMethod === 'cash';
  const isStripe = paymentMethod === 'stripe';
  const isExternal = paymentMethod === 'venmo' || paymentMethod === 'zelle';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="post-reg-payment-prompt"
        className="sm:max-w-md flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="pr-6 flex items-center gap-2">
            <CheckCircle
              size={20}
              weight="fill"
              className="text-emerald-600 shrink-0"
            />
            <span>You&rsquo;re registered!</span>
          </DialogTitle>
          <DialogDescription>
            This {eventTypeLabel} has an entry fee of{' '}
            <span className="font-semibold text-[#013523]" data-testid="post-reg-amount">
              {amountLabel}
            </span>
            .{' '}
            {isCash
              ? 'Payment Due at Event.'
              : 'You can pay now or anytime before the event begins.'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          {isCash && (
            <div
              data-testid="post-reg-cash-note"
              className="rounded-xl border border-[#c9a93a]/40 bg-[#fff9e6]/60 px-3 py-2.5 text-[13px] text-[#3a2c05]"
            >
              Bring cash to the event. No online payment is required.
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          {isCash ? (
            <Button
              onClick={onPayLater}
              className="bg-[#013523] text-white hover:bg-[#024029]"
              data-testid="post-reg-cash-ok"
            >
              Got it
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onPayLater}
                data-testid="post-reg-pay-later"
              >
                I&rsquo;ll Pay Later
              </Button>
              <Button
                onClick={onPayNow}
                className="bg-[#013523] text-white hover:bg-[#024029] inline-flex items-center gap-1.5"
                data-testid="post-reg-pay-now"
              >
                {isStripe && <CreditCard size={14} weight="bold" />}
                Pay {amountLabel} Now
                {isExternal && <ArrowSquareOut size={12} weight="bold" />}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
